import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import LoadingSpinner from "../../common/loaders/loadingSpinner";

import { BreadCrumbs } from "../../common/layout";
import useApi from "../../../hooks/useApi";

import prospectAssessmentService from "../../../services/prospectAssessmentServices";
import Table from "../../common/tables/ActionTable";

import "../../../styles/react-table.css";
import "react-table/react-table.css";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Docs Requested", value: "Docs Requested" },
  { label: "Docs Pending", value: "Docs Pending" },
  { label: "Docs Complete", value: "Docs Complete" },
  { label: "Under Review", value: "Under Review" },
];

const LINK_STATE_BADGE = {
  Active: "badge badge-success",
  Expired: "badge badge-warning",
  Invalidated: "badge badge-dark",
};

const tableColumns = [
  {
    Header: "Organisation",
    accessor: "organisation",
    headerStyle: { textAlign: "left" },
        Cell: (row) =>
        row.original.prospect_guid ? (
          <Link
            className="link"
            to={{
              pathname: "/prospects/details/assessment",
              search: `?a_guid=${row.original.prospect_guid}`,
            }}
          >
            {row.original.organisation || "-"}
          </Link>
        ) : (
          row.original.organisation || "-"
        ),

  },
  {
    Header: "Contact",
    accessor: "contact_name",
    headerStyle: { textAlign: "left" },
    Cell: (row) => (
      <>
        <div>{row.original.contact_name || "-"}</div>
        <small className="text-muted">
          {row.original.contact_email || ""}
        </small>
      </>
    ),
  },
  {
    Header: "Sent To",
    accessor: "sent_to_email",
    headerStyle: { textAlign: "left" },
    Cell: (row) =>
      row.original.sent_to_email || (
        <span className="text-muted">Never sent</span>
      ),
  },
  {
    Header: "Status",
    accessor: "status",
    headerStyle: { textAlign: "left" },
  },
  {
    Header: "Link State",
    accessor: "link_state",
    headerStyle: { textAlign: "left" },
    Cell: (row) => {
      const state = row.original.link_state;
      return (
        <span className={LINK_STATE_BADGE[state] || "badge badge-secondary"}>
          {state}
        </span>
      );
    },
  },
  {
    Header: "Expires",
    accessor: "expires_at",
    headerStyle: { textAlign: "left" },
    Cell: (row) => (
      <>
        <div>{row.original.expires_at || "-"}</div>
        {row.original.link_state === "Active" && (
          <small className="text-muted">
            {row.original.days_left} day
            {row.original.days_left === 1 ? "" : "s"} left
          </small>
        )}
      </>
    ),
  },
  {
    Header: "Created",
    accessor: "created_at",
    headerStyle: { textAlign: "left" },
  },
];

/**
 * Cross-prospect Document Collection Report. Every collection link ever
 * generated, across every prospect. Backed by
 * DocumentCollectionController::actionFetchCollectionReport, which is
 * also restricted server-side (assertReportAccess) to International/
 * Country Office staff.
 *
 * Search is handled by <Table /> itself via service + searchService
 * (same endpoint for both — the backend already branches on the
 * presence of a search term within one action, so no separate search
 * endpoint is needed) — no hand-rolled search box/state here, matching
 * how the Introducers Index page and TeamMembersTable both defer search
 * entirely to Table.
 *
 * link_state (Active/Expired/Invalidated) is shown as a read-only column
 * but is intentionally NOT filterable yet — that's a planned follow-up,
 * matching the backend note in actionFetchCollectionReport.
 */
function DocumentCollectionReport() {
  const tableRef = useRef(null);

  const [status, setStatus] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const fetchCollectionReportApi = useApi(
    prospectAssessmentService.fetchCollectionReport
  );

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      const response = await fetchCollectionReportApi.request({
        status: status || null,
        download: true,
      });

      // actionGenerateExcelReport returns a raw file path/URL string
      // (not wrapped in the usual {success, message, data} envelope),
      // so response.data IS the URL directly — same as
      // AllTeamMembers::handleExportToExcel.
      if (response?.data) {
        window.open(response.data, "_blank", "noreferrer");
      } else {
        toast.error("Failed to export the collection report.");
      }
    } catch (error) {
      toast.error("Failed to export the collection report.");
    } finally {
      setIsExporting(false);
    }
  };

  // Reload on filter change — same pattern as the Introducers Index page's
  // `useEffect(() => { if (owner ...) tableRef.current.reloadData(); }, [owner]);`
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.reloadData();
    }
  }, [status]);

  const RightPanel = () => (
    <button
      type="button"
      className="btn btn-primary pull-right m-l-sm"
      onClick={handleExportToExcel}
      disabled={isExporting}
    >
      <i className="fa fa-download" />{" "}
      {isExporting ? "Preparing..." : "Download Report"}
    </button>
  );

  const LeftPanel = () => (
    <select
      className="form-control input-sm"
      value={status}
      onChange={handleStatusChange}
    >
      {STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <Helmet>
        <title>Document Collection Report | {global.config.title}</title>
      </Helmet>

      <BreadCrumbs
        Header="Document Collection Report"
        activeModule="Document Collection Report"
        Route1="/dashboard"
        Label1="Dashboard"
      />

      {isExporting && <LoadingSpinner />}

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">DOCUMENT COLLECTION REPORT</h4>
        </div>

        <div className="card-body">
          <Table
            ref={tableRef}
            reqParams={{
              status: status || null,
            }}
            service={prospectAssessmentService.fetchCollectionReport}
            searchService={prospectAssessmentService.fetchCollectionReport}
            cols={tableColumns}
            uid={"request_guid"}
            tableRightPanel={<RightPanel />}
            tableLeftPanel={<LeftPanel />}
            headerTabs={[]}
          />
        </div>
      </div>
    </>
  );
}

export default DocumentCollectionReport;